/**
 * EfrisItemMappingDesk.tsx
 *
 * Authority Product Code & EAC-CET Commodity Mapping Console (Phase INV-02F):
 * - Maps internal inventory SKUs to URA EFRIS commodity codes and standardized package units.
 * - Searches against local East African Community (EAC) Common External Tariff cache.
 * - Displays mapping status (active / pending / retired) and prevents unmapped stock clearances.
 */

import React, { useState } from 'react';
import type { AuthorityProductMapping } from '../../plugins/invoicing/compliance/uganda/authority-product-mapper.js';

export interface EfrisItemMappingDeskProps {
  readonly mappings: readonly AuthorityProductMapping[];
  readonly onSaveMapping: (mapping: AuthorityProductMapping) => Promise<void>;
}

export const EfrisItemMappingDesk: React.FC<EfrisItemMappingDeskProps> = ({ mappings, onSaveMapping }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMapping, setSelectedMapping] = useState<AuthorityProductMapping | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form State
  const [internalSku, setInternalSku] = useState('');
  const [authorityProductCode, setAuthorityProductCode] = useState('');
  const [classificationCode, setClassificationCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [packageUnitCode, setPackageUnitCode] = useState<'PCE' | 'KGM' | 'LTR' | 'BAG' | 'BOX' | 'TON'>('PCE');
  const [taxRateCategory, setTaxRateCategory] = useState<'standard_18' | 'zero_rated_0' | 'exempt'>('standard_18');

  const filteredMappings = mappings.filter(
    (m) =>
      m.internalSku.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.authorityProductCode.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenNew = () => {
    setSelectedMapping(null);
    setInternalSku('');
    setAuthorityProductCode('');
    setClassificationCode('0901210000');
    setItemName('');
    setPackageUnitCode('PCE');
    setTaxRateCategory('standard_18');
    setIsEditing(true);
  };

  const handleEdit = (m: AuthorityProductMapping) => {
    setSelectedMapping(m);
    setInternalSku(m.internalSku);
    setAuthorityProductCode(m.authorityProductCode);
    setClassificationCode(m.classificationCode || '');
    setItemName(m.itemName);
    setPackageUnitCode(m.packageUnitCode);
    setTaxRateCategory(m.taxRateCategory);
    setIsEditing(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const mapping: AuthorityProductMapping = {
      tenantId: selectedMapping?.tenantId || 'tenant-current',
      internalSku: internalSku.trim(),
      authority: 'EFRIS',
      authorityProductCode: authorityProductCode.trim(),
      classificationCode: classificationCode.trim() || undefined,
      classificationSystem: 'EAC-CET',
      itemName: itemName.trim(),
      packageUnitCode,
      taxRateCategory,
      stockTrackingRequired: true,
      status: 'active',
      effectiveFrom: new Date().toISOString(),
    };

    await onSaveMapping(mapping);
    setIsEditing(false);
  };

  return (
    <div className="flex flex-col h-full bg-slate-950 text-slate-100 p-6 space-y-6 font-mono select-none overflow-y-auto">
      {/* Header */}
      <header className="flex justify-between items-center bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
        <div>
          <span className="text-xs uppercase font-bold text-amber-400 tracking-widest">Uganda URA EFRIS Master</span>
          <h1 className="text-2xl font-black text-white mt-1">Authority Product Code Mappings</h1>
          <p className="text-xs text-slate-400">Decoupled SKU Master, EAC-CET Classifications & Package Units</p>
        </div>

        <button
          onClick={handleOpenNew}
          className="px-5 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs shadow-lg shadow-amber-400/20 active:scale-95 transition-all"
        >
          + Map New Product
        </button>
      </header>

      {/* Search Bar */}
      <div className="flex gap-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by SKU, Product Name, or EFRIS Code..."
          className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-400"
        />
      </div>

      {/* Mappings Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-950 text-slate-400 uppercase text-[10px] border-b border-slate-800">
            <tr>
              <th className="p-4">Internal SKU</th>
              <th className="p-4">EFRIS Product Code</th>
              <th className="p-4">Item Name</th>
              <th className="p-4">EAC-CET (HS)</th>
              <th className="p-4">Package Unit</th>
              <th className="p-4">Tax Rate</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {filteredMappings.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-slate-500">
                  No authority product mappings found. Map your internal SKUs to prevent EFRIS clearance errors.
                </td>
              </tr>
            ) : (
              filteredMappings.map((m) => (
                <tr key={`${m.tenantId}-${m.internalSku}`} className="hover:bg-slate-800/40 transition-colors">
                  <td className="p-4 font-bold text-white">{m.internalSku}</td>
                  <td className="p-4 font-mono text-amber-400">{m.authorityProductCode}</td>
                  <td className="p-4 text-slate-200">{m.itemName}</td>
                  <td className="p-4 font-mono text-slate-400">{m.classificationCode || '—'}</td>
                  <td className="p-4 font-bold text-emerald-400">{m.packageUnitCode}</td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        m.taxRateCategory === 'standard_18'
                          ? 'bg-blue-500/20 text-blue-300'
                          : m.taxRateCategory === 'zero_rated_0'
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-amber-500/20 text-amber-300'
                      }`}
                    >
                      {m.taxRateCategory === 'standard_18' ? '18% VAT' : m.taxRateCategory === 'zero_rated_0' ? '0% Zero' : 'Exempt'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold capitalize ${
                        m.status === 'active'
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-rose-500/20 text-rose-300'
                      }`}
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => handleEdit(m)}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-black text-white">
              {selectedMapping ? 'Edit Authority Mapping' : 'Map Internal SKU to URA EFRIS'}
            </h3>

            <form onSubmit={handleSave} className="space-y-3 text-xs">
              <div>
                <label className="text-slate-300 font-bold uppercase text-[10px]">Internal SKU *</label>
                <input
                  type="text"
                  value={internalSku}
                  onChange={(e) => setInternalSku(e.target.value)}
                  placeholder="e.g. SKU-COFFEE-250G"
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-white"
                  required
                />
              </div>

              <div>
                <label className="text-slate-300 font-bold uppercase text-[10px]">URA EFRIS Product Code *</label>
                <input
                  type="text"
                  value={authorityProductCode}
                  onChange={(e) => setAuthorityProductCode(e.target.value)}
                  placeholder="e.g. EFRIS-PRD-102938"
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-white"
                  required
                />
              </div>

              <div>
                <label className="text-slate-300 font-bold uppercase text-[10px]">Product Name *</label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="e.g. Roasted Arabica Coffee 250g"
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-300 font-bold uppercase text-[10px]">EAC-CET (10-Digit HS)</label>
                  <input
                    type="text"
                    value={classificationCode}
                    onChange={(e) => setClassificationCode(e.target.value)}
                    placeholder="e.g. 0901210000"
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-bold uppercase text-[10px]">Package Unit *</label>
                  <select
                    value={packageUnitCode}
                    onChange={(e) => setPackageUnitCode(e.target.value as any)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-white"
                  >
                    <option value="PCE">PCE (Piece)</option>
                    <option value="KGM">KGM (Kilogram)</option>
                    <option value="LTR">LTR (Liter)</option>
                    <option value="BAG">BAG (Bag)</option>
                    <option value="BOX">BOX (Box)</option>
                    <option value="TON">TON (Tonne)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-slate-300 font-bold uppercase text-[10px]">Tax Category *</label>
                <select
                  value={taxRateCategory}
                  onChange={(e) => setTaxRateCategory(e.target.value as any)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-white"
                >
                  <option value="standard_18">Standard Rate (18% VAT)</option>
                  <option value="zero_rated_0">Zero-Rated (0% VAT)</option>
                  <option value="exempt">Exempt from VAT</option>
                </select>
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-800 text-slate-400 font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 rounded-xl bg-amber-400 text-slate-950 font-black"
                >
                  Save Mapping
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
